import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * Two kinds of value live in these fields, and they need opposite treatment.
 *
 *   A CALENDAR DAY — deal start/end, product start/end, due dates, launch
 *   dates. Somebody picked a day on a calendar. "1 September" means the same
 *   thing in Cleveland and in Denver, so it must render identically in both.
 *
 *   AN INSTANT — created_at, lastModified, timeCompleted, signed_at. A real
 *   moment. Rendering it in the reader's own timezone is correct, and is what
 *   they expect.
 *
 * The bug this addresses is calendar days stored as instants. A picker in
 * Eastern serialised 1 September as 2026-09-01T04:00:00.000Z — local midnight.
 * Read back in Central that is 31 August 23:00, so two people looking at the
 * same contract saw different start dates. 2,527 product rows carry a value in
 * the 04:00-07:00 UTC band where US readers disagree.
 *
 * The canonical form for a calendar day is NOON UTC. Not date-only, and not
 * midnight: noon is the only anchor that survives being formatted naively in
 * any US timezone, and it is already what 8,236 rows use. `toCalendarDate`
 * writes it; `formatDateString` reads any of the historical shapes back as the
 * day that was intended.
 */
const CALENDAR_DAY_HOUR = 12

/**
 * Normalise a picked day to the canonical calendar-day form. Use this wherever
 * a date picker's value is about to be persisted, so no new row can encode the
 * author's timezone.
 */
export const toCalendarDate = (value: unknown): string | null => {
  if (value == null || value === '') return null
  // Read the day as the picker's own local day, which is the day the user saw
  // and clicked, then re-anchor it at noon UTC.
  const local = dayjs.isDayjs(value) ? value : dayjs(value as string)
  if (!local.isValid()) return null
  return dayjs
    .utc(`${local.format('YYYY-MM-DD')}T00:00:00.000Z`)
    .hour(CALENDAR_DAY_HOUR)
    .toISOString()
}

export const formatDayjsDateTime = dateStr => {
  if (dateStr) {
    return dayjs(dateStr).format('MM/DD/YYYY HH:mm:ss')
  } else {
    return null
  }
}
export const formatDayjsDate = date => {
  return dayjs(date).format('MM/DD/YYYY')
}
export const formatDateTime = date => {
  if (date) {
    return dayjs(date).format('MM/DD/YYYY hh:mm A')
  } else {
    return null
  }
}
export const formatTimeAndSeconds = date => {
  if (date) {
    return dayjs(date).format('hh:mm:ss A') // e.g., "03:58:20 PM"
  } else {
    return null
  }
}
export const formatSecondsAsMinutesAndSeconds = ttl => {
  if (ttl == null) return null
  const minutes = Math.floor(ttl / 60)
  const seconds = ttl % 60
  return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`
}
export const formatISODate = date => {
  if (date) {
    return dayjs(date).toISOString()
  } else {
    return null
  }
}
export const formatDayJsTime = dateStr => {
  if (dateStr) {
    return dayjs(dateStr).format('hh:mm A')
  } else {
    return null
  }
}
/**
 * Render a CALENDAR DAY. Formats in UTC, which recovers the intended day from
 * every shape the data has ever been written in:
 *
 *   2026-09-01                    date-only          -> 09/01/2026
 *   2026-09-01T04:00:00.000Z      midnight Eastern   -> 09/01/2026
 *   2026-09-01T05:00:00.000Z      midnight Central   -> 09/01/2026
 *   2026-09-01T12:00:00.000Z      canonical          -> 09/01/2026
 *
 * All of them sit inside the same UTC day, which is exactly why UTC is the
 * right lens: local midnight anywhere in the US lands at 04:00-08:00Z on the
 * intended date. Formatting locally is what split the two readers apart.
 *
 * For a real timestamp use `formatDayjsModifiedTime` instead — an instant
 * SHOULD move with the reader's timezone.
 */
export const formatDateString = date => {
  if (!date) return ''
  // A dayjs value here came from a picker and is already the user's own day;
  // reading its local calendar day is correct. Stored strings are read as UTC.
  if (dayjs.isDayjs(date)) return date.format('MM/DD/YYYY')
  const d = dayjs.utc(date)
  return d.isValid() ? d.format('MM/DD/YYYY') : ''
}

/**
 * Parse a YYYY-MM-DD date-only string as a JS Date anchored at LOCAL midnight
 * of that calendar day. Falls through to `new Date(value)` for anything else
 * (full ISO datetimes, Date instances, dayjs objects).
 *
 * Why this exists: `new Date('2026-05-01')` parses as UTC midnight per the
 * ECMAScript spec, which in any timezone west of UTC renders as the prior
 * day's evening locally. For fields that represent a *calendar day* (deal
 * start/end, etc.) we want "May 1 means May 1 everywhere" — so we treat the
 * date-only string as local-midnight instead of UTC-midnight.
 */
export const parseDateLocal = (value: unknown): Date | null => {
  if (value == null || value === '') return null
  if (value instanceof Date) return value
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (m) {
      return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10))
    }
  }
  const d = new Date(value as string)
  return Number.isFinite(d.getTime()) ? d : null
}
export const formatDayjsModifiedTime = dateStr => {
  if (dateStr) {
    return dayjs(dateStr).format('MM/DD/YYYY hh:mm A')
  } else {
    return null
  }
}
export const formatMonthYear = value => {
  const parsed = dayjs(value, 'YYYY-MM', true)
  return parsed.isValid() ? parsed.format('MMMM YYYY') : value
}
export const isActiveThisMonth = (start, end) => {
  const today = new Date()
  const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()))
  try {
    const startDate = new Date(start)
    const endDate = new Date(end)
    return startDate <= todayUTC && endDate >= todayUTC
  } catch (e) {
    console.warn(`❌ Invalid date in product: start=${start}, end=${end}`, e)
    return false
  }
}
export const isActiveProduct = (start, end) => {
  const today = new Date()
  const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()))

  try {
    const _startDate = new Date(start)
    const endDate = new Date(end)

    // Product is currently active or will be in the future
    return endDate >= todayUTC
  } catch (e) {
    console.warn(`❌ Invalid date in product: start=${start}, end=${end}`, e)
    return false
  }
}
